import { z } from 'zod';

export const ORDER_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'quoted',
  'approved',
  'in_production',
  'ready',
  'completed',
  'cancelled',
] as const;

export const ORDER_TYPES = ['order', 'quote'] as const;
export const ORDER_KINDS = ['fronts', 'cabinets'] as const;
export type OrderKind = (typeof ORDER_KINDS)[number];
export const CUSTOMER_TYPES = ['home_owner', 'contractor', 'cabinet_shop', 'designer', 'property_developer', 'dealer'] as const;
export const SHIPPING_METHODS = ['pickup', 'quick_ship', 'route_truck'] as const;
export const MATERIALS = ['veneer', 'raw_mdf_hdf', 'laminate', 'acrylic'] as const;
export const STYLES = ['shaker_5pc', 'flat_panel', 'shaker_1pc'] as const;
export const TEXTURES = ['vertical', 'horizontal', 'none'] as const;
export const ITEM_TYPES = ['door', 'drawer_front'] as const;
export const HINGE_SIDES = ['left', 'right', 'both'] as const;
export const HINGE_ANGLES = ['105', '165'] as const;
export const DRAWER_TYPES = ['laminate', 'plywood', 'metal'] as const;
export const FILE_KINDS = ['photo', 'drawing', 'document'] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];
export type OrderType = (typeof ORDER_TYPES)[number];
export type CustomerType = (typeof CUSTOMER_TYPES)[number];
export type ShippingMethod = (typeof SHIPPING_METHODS)[number];
export type Material = (typeof MATERIALS)[number];
export type OrderStyle = (typeof STYLES)[number];
export type Texture = (typeof TEXTURES)[number];
export type ItemType = (typeof ITEM_TYPES)[number];
export type HingeSide = (typeof HINGE_SIDES)[number];
export type HingeAngle = (typeof HINGE_ANGLES)[number];
export type DrawerType = (typeof DRAWER_TYPES)[number];
export type FileKind = (typeof FILE_KINDS)[number];

// ── order item ─────────────────────────────────────────────────────────────────
export const orderItemSchema = z.object({
  id: z.string().uuid().optional(),
  sort_order: z.number().int().default(0),
  item_type: z.enum(ITEM_TYPES),
  width_inches: z.number().positive().multipleOf(0.01),
  height_inches: z.number().positive().multipleOf(0.01),
  quantity: z.number().int().positive(),
  notes: z.string().nullable().optional(),
  // Hardware fields (door only)
  hinges_needed: z.boolean().default(false).optional(),
  hinges_quantity: z.number().int().positive().nullable().optional(),
  hinges_positions: z.array(z.number().positive()).min(2).max(10).nullable().optional(),
  hinges_offset_inches: z.number().min(3.5).nullable().optional(),
  hinges_side: z.enum(HINGE_SIDES).nullable().optional(),
  hinges_angle: z.enum(HINGE_ANGLES).nullable().optional(),
  // Hardware fields (drawer_front only)
  drawer_box_needed: z.boolean().default(false).optional(),
  drawer_box_type: z.enum(DRAWER_TYPES).nullable().optional(),
});

export type OrderItemInput = z.infer<typeof orderItemSchema>;

// ── order group ────────────────────────────────────────────────────────────────
export const orderGroupSchema = z.object({
  id: z.string().uuid().optional(),
  sort_order: z.number().int().default(0),
  material: z.enum(MATERIALS),
  style: z.enum(STYLES),
  texture: z.enum(TEXTURES).nullable().optional(),
  notes: z.string().nullable().optional(),
  items: z.array(orderItemSchema).default([]),
});

export type OrderGroupInput = z.infer<typeof orderGroupSchema>;

// ── order hardware ─────────────────────────────────────────────────────────────
export const orderHardwareSchema = z.object({
  hinges_needed: z.boolean().default(false),
  hinges_quantity: z.number().int().positive().nullable().optional(),
  hinges_side: z.enum(HINGE_SIDES).nullable().optional(),
  hinges_angle: z.enum(HINGE_ANGLES).nullable().optional(),
  drawers_needed: z.boolean().default(false),
  drawer_type: z.enum(DRAWER_TYPES).nullable().optional(),
  notes: z.string().nullable().optional(),
});

export type OrderHardwareInput = z.infer<typeof orderHardwareSchema>;

// ── create order (just type; everything else is nullable for draft) ────────────
export const createOrderSchema = z.object({
  type: z.enum(ORDER_TYPES),
  kind: z.enum(ORDER_KINDS).default('fronts'),
  // Staff-only: create the order on behalf of this customer (a profiles.id). Ignored for non-staff.
  customer_id: z.string().uuid().optional(),
  // Staff-only: create and immediately link this order to a CRM client.
  crm_client_id: z.string().uuid().optional(),
  // Optional initial job name (used by the staff create-for-client flow).
  job_name: z.string().max(200).optional(),
});

export type CreateOrderInput = z.input<typeof createOrderSchema>;

// ── patch order (all fields optional — sent on each autosave) ─────────────────
export const patchOrderSchema = z.object({
  customer_type: z.enum(CUSTOMER_TYPES).optional(),
  job_name: z.string().max(200).optional(),
  po_number: z.string().max(100).nullable().optional(),
  requested_completion_date: z.string().date().nullable().optional(),
  contact_first_name: z.string().max(100).optional(),
  contact_last_name: z.string().max(100).optional(),
  contact_company: z.string().max(200).nullable().optional(),
  contact_phone: z.string().max(30).optional(),
  contact_email: z.string().email().optional(),
  contact_address: z.string().max(500).optional(),
  project_address: z.string().max(500).nullable().optional(),
  shipping_method: z.enum(SHIPPING_METHODS).nullable().optional(),
  shipping_address: z.string().max(500).nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
  internal_notes: z.string().max(5000).nullable().optional(),
  total_estimate_cents: z.number().int().nonnegative().nullable().optional(),
  labor_discount_pct: z.number().min(0).max(100).optional(),
});

export type PatchOrderInput = z.infer<typeof patchOrderSchema>;

// ── submit validation (required fields for a complete order) ──────────────────
export const submitOrderSchema = z.object({
  customer_type: z.enum(CUSTOMER_TYPES),
  job_name: z.string().min(1).max(200),
  contact_first_name: z.string().min(1).max(100),
  contact_last_name: z.string().min(1).max(100),
  contact_phone: z.string().min(7).max(30),
  contact_email: z.string().email(),
  contact_address: z.string().min(5).max(500),
  shipping_method: z.enum(SHIPPING_METHODS),
  shipping_address: z.string().max(500).nullable().optional(),
}).refine(
  (d) => d.shipping_method === 'pickup' || (d.shipping_address != null && d.shipping_address.length > 0),
  { message: 'Shipping address required when shipping method is not pickup', path: ['shipping_address'] },
);

// Cabinet orders are assembled from catalog lines and may be created for CRM-only clients.
// Their line-item validation happens in the submit endpoint, so they do not require the
// fronts-order contact form fields that are not editable in the cabinet builder.
export const submitCabinetOrderSchema = z.object({
  kind: z.literal('cabinets'),
  customer_type: z.enum(CUSTOMER_TYPES),
});

export function submissionSchemaForKind(kind: OrderKind) {
  return kind === 'cabinets' ? submitCabinetOrderSchema : submitOrderSchema;
}

// ── change status ─────────────────────────────────────────────────────────────
export const changeStatusSchema = z.object({
  to_status: z.enum(ORDER_STATUSES),
  note: z.string().max(1000).nullable().optional(),
});

export type ChangeStatusInput = z.infer<typeof changeStatusSchema>;

// ── group CRUD ────────────────────────────────────────────────────────────────
export const createGroupSchema = z.object({
  sort_order: z.number().int().default(0),
  material: z.enum(MATERIALS),
  style: z.enum(STYLES),
  texture: z.enum(TEXTURES).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const patchGroupSchema = createGroupSchema.partial();

// ── item CRUD ─────────────────────────────────────────────────────────────────
export const createItemSchema = z.object({
  sort_order: z.number().int().default(0),
  item_type: z.enum(ITEM_TYPES),
  width_inches: z.number().positive().max(120),
  height_inches: z.number().positive().max(120),
  quantity: z.number().int().positive().max(9999),
  notes: z.string().max(500).nullable().optional(),
  hinges_needed: z.boolean().optional(),
  hinges_quantity: z.number().int().positive().nullable().optional(),
  hinges_positions: z.array(z.number().positive()).min(2).max(10).nullable().optional(),
  hinges_offset_inches: z.number().min(3.5).nullable().optional(),
  hinges_side: z.enum(HINGE_SIDES).nullable().optional(),
  hinges_angle: z.enum(HINGE_ANGLES).nullable().optional(),
  drawer_box_needed: z.boolean().optional(),
  drawer_box_type: z.enum(DRAWER_TYPES).nullable().optional(),
});

export const patchItemSchema = createItemSchema.partial().extend({
  item_type: z.enum(ITEM_TYPES).optional(),
  width_inches: z.number().positive().max(120).optional(),
  height_inches: z.number().positive().max(120).optional(),
  quantity: z.number().int().positive().max(9999).optional(),
});

// ── hardware upsert ───────────────────────────────────────────────────────────
export const upsertHardwareSchema = orderHardwareSchema;

// ── API response shapes ───────────────────────────────────────────────────────
export type OrderFileRow = {
  id: string;
  order_id: string;
  uploaded_by: string;
  r2_key: string;
  filename: string;
  mime_type: string;
  file_size_bytes: number;
  width_px: number | null;
  height_px: number | null;
  kind: FileKind;
  sort_order: number;
  created_at: string;
  signed_url?: string;
  download_url?: string;
};

export type OrderItemRow = {
  id: string;
  group_id: string;
  sort_order: number;
  item_type: ItemType;
  width_inches: number;
  height_inches: number;
  quantity: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Hardware fields (moved from order-level to item-level)
  hinges_needed: boolean | null;
  hinges_quantity: number | null;
  hinges_positions: number[] | null;
  hinges_offset_inches: number | null;
  hinges_side: HingeSide | null;
  hinges_angle: HingeAngle | null;
  drawer_box_needed: boolean | null;
  drawer_box_type: DrawerType | null;
};

export type OrderGroupRow = {
  id: string;
  order_id: string;
  sort_order: number;
  material: Material;
  style: OrderStyle;
  texture: Texture | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  items: OrderItemRow[];
};

export type OrderHardwareRow = {
  id: string;
  order_id: string;
  hinges_needed: boolean;
  hinges_quantity: number | null;
  hinges_side: HingeSide | null;
  hinges_angle: HingeAngle | null;
  drawers_needed: boolean;
  drawer_type: DrawerType | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type OrderStatusHistoryRow = {
  id: string;
  order_id: string;
  from_status: OrderStatus | null;
  to_status: OrderStatus;
  changed_by: string;
  note: string | null;
  created_at: string;
};

export type OrderRow = {
  id: string;
  order_number: string | null;
  type: OrderType;
  kind: OrderKind;
  parent_quote_id: string | null;
  status: OrderStatus;
  customer_id: string | null;
  created_by: string;
  customer_type: CustomerType;
  job_name: string;
  po_number: string | null;
  requested_completion_date: string | null;
  contact_first_name: string;
  contact_last_name: string;
  contact_company: string | null;
  contact_phone: string;
  contact_email: string;
  contact_address: string;
  project_address: string | null;
  shipping_method: ShippingMethod | null;
  shipping_address: string | null;
  notes: string | null;
  internal_notes: string | null;
  submitted_at: string | null;
  total_estimate_cents: number | null;
  labor_discount_pct: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  groups?: OrderGroupRow[];
  /** @deprecated Hardware moved to item level (Change 7). Will be null after migration. */
  hardware?: OrderHardwareRow | null;
  files?: OrderFileRow[];
  status_history?: OrderStatusHistoryRow[];
  editability?: { can_edit_content: boolean; can_add_files: boolean };
};

// ── Pricing ───────────────────────────────────────────────────────────────────

export type PricingRuleRow = {
  id: string;
  material: Material;
  material_per_sqft: number;
  labor_per_sqft: number;
  updated_by: string | null;
  updated_at: string;
  created_at: string;
};

export type CustomerTypeDiscountRow = {
  id: string;
  customer_type: CustomerType;
  labor_discount_pct: number;
  updated_by: string | null;
  updated_at: string;
  created_at: string;
};

export const TERMS_VERSION = '2026-05';
